import type { ApartmentRooms, PropertyType, TransactionType } from "../../../types";
import type { CitySource, StreetSource } from "../location/types";

export const MARKET_METHODOLOGY_VERSION = "CMA-0.4" as const;

export type MarketAnalysisStatus = "draft" | "finalized";
export type MarketConfidence = "low" | "medium" | "high";
export type OwnerPricePosition = "below" | "within" | "above" | "unknown";
export type CanonicalMarketPropertyType = "apartment" | "house" | "land" | "recreational" | "commercial" | "garage" | "development" | "other";
export type CanonicalCondition = "original" | "partial_reconstruction" | "full_reconstruction" | "new_build" | "shell" | "unknown";
export type MarketAreaBasis = "interior" | "apartment" | "total_only" | "legacy_unspecified";
export type MarketTriState = "yes" | "no" | "unknown";

export type MarketLocationHierarchy = {
  country?: string;
  region?: string;
  district?: string;
  municipality: string;
  municipalityNormalized: string;
  municipalityId?: string;
  locality?: string;
  microlocation?: string;
  street?: string;
  building?: string;
  address?: string;
  postalCode?: string;
  sourceLocationCode?: string;
};

export type MarketAreaModel = {
  apartmentArea?: number;
  interiorArea?: number;
  balconyArea?: number;
  loggiaArea?: number;
  cellarArea?: number;
  terraceArea?: number;
  totalArea?: number;
  areaBasis: MarketAreaBasis;
};

export type MarketTechnicalModel = {
  condition: CanonicalCondition;
  electrical?: string;
  coreBathroom?: string;
  kitchen?: string;
  windows?: string;
  floors?: string;
  doors?: string;
  buildingCondition?: string;
};

export type MarketEquipmentModel = {
  balcony?: MarketTriState;
  loggia?: MarketTriState;
  terrace?: MarketTriState;
  cellar?: MarketTriState;
  parking?: MarketTriState;
  airConditioning?: MarketTriState;
  orientation?: string;
};

export type MarketFieldProvenance = Record<string, {
  source: string;
  confidence: MarketConfidence;
  capturedAt: string;
  rawValue?: string | number | boolean;
}>;

export type MarketPropertySnapshot = {
  city: string;
  cityNormalized?: string;
  citySource?: CitySource;
  municipalityId?: string;
  locality: string;
  street: string;
  streetNormalized?: string;
  streetSource?: StreetSource;
  streetId?: string;
  propertyType: PropertyType | string;
  rooms: ApartmentRooms | string;
  area?: number;
  condition: string;
  reconstruction: string;
  electricity: string;
  coreType: string;
  kitchen: string;
  balconyType: string;
  cellar: string;
  floor?: number;
  totalFloors?: number;
  elevator: string;
  orientation: string;
  buildingCondition: string;
  buildingType?: string;
  location?: MarketLocationHierarchy;
  areas?: MarketAreaModel;
  technical?: MarketTechnicalModel;
  equipment?: MarketEquipmentModel;
  canonicalPropertyType?: CanonicalMarketPropertyType;
  fieldProvenance?: MarketFieldProvenance;
};

export type MarketSubjectSnapshot = MarketPropertySnapshot & {
  transactionType?: TransactionType;
  askingPriceAtAnalysis?: number;
};

export const SIMILARITY_FACTORS = [
  "location",
  "area",
  "propertyType",
  "rooms",
  "technicalCondition",
  "recency",
  "floorAndElevator",
  "equipment"
] as const;

export type SimilarityFactor = (typeof SIMILARITY_FACTORS)[number];

export type SimilarityComponent = {
  weight: number;
  available: boolean;
  score?: number;
};

export type ComparableDataConfidence = {
  score: number;
  level: MarketConfidence;
  reason: string;
  knownAttributes: string[];
  unknownAttributes: string[];
};

export type SimilarityScoreBreakdown = {
  methodologyVersion: string;
  components: Record<SimilarityFactor, SimilarityComponent>;
  availableWeight: number;
  completeness: number;
  completenessPenalty: number;
  normalizedScore: number;
  finalScore: number;
  selectionScore: number;
  valuationWeightScore: number;
  dataConfidence: ComparableDataConfidence;
  explanations: string[];
};

export const MARKET_COMPARABLE_REJECTION_REASONS = [
  "iná lokalita",
  "zlý technický match",
  "novostavba",
  "atypický byt",
  "nereálna cena",
  "duplicita",
  "iné"
] as const;

export type MarketComparableRejectionReason = (typeof MARKET_COMPARABLE_REJECTION_REASONS)[number];
export type MarketComparableDecision = "included" | "excluded";
export type MarketComparableOrigin = "manual" | "reality_radar" | "live_provider";
export type MarketComparableRole = "primary" | "competition";
export type MarketContextGroup = "same_condition" | "other_condition";

export type MarketComparableDraft = MarketPropertySnapshot & {
  origin?: MarketComparableOrigin;
  marketRole?: MarketComparableRole;
  marketContextGroup?: MarketContextGroup;
  primaryQualificationReasons?: string[];
  askingPrice?: number;
  realizedPrice?: number;
  listingStatus: string;
  sourceName: string;
  sourceUrl: string;
  capturedAt: string;
  sourceSnapshot?: Record<string, unknown>;
};

export type MarketAnalysisComparable = MarketComparableDraft & {
  origin: MarketComparableOrigin;
  marketRole?: MarketComparableRole;
  id: string;
  analysisId: string;
  userId: string;
  sortOrder: number;
  sourceHash: string;
  similarityScore: number;
  scoreBreakdown: SimilarityScoreBreakdown;
  decision: MarketComparableDecision;
  rejectionReason?: MarketComparableRejectionReason;
  createdAt: string;
  updatedAt: string;
};

export type MarketDatasetSummary = {
  count: number;
  medianPricePerM2: number;
  weightedPricePerM2: number;
  low: number;
  high: number;
  dispersion: number;
};

export type MarketPriceDistribution = {
  count: number;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
};

export type MarketFunnelStage = {
  count: number;
  priceDistribution?: MarketPriceDistribution;
  subjectPricePercentile?: number;
  subjectPositionText?: string;
};

export const MARKET_FUNNEL_REJECTION_REASONS = [
  "area_difference",
  "different_condition",
  "insufficient_locality",
  "low_confidence",
  "low_similarity",
  "low_selection_score",
  "inactive_or_invalid",
  "duplicate",
  "other"
] as const;

export type MarketFunnelRejectionReason = (typeof MARKET_FUNNEL_REJECTION_REASONS)[number];

export type MarketFunnelRejectionSample = {
  id: string;
  title: string;
  area?: number;
  askingPrice?: number;
  reason: MarketFunnelRejectionReason;
};

export type MarketFunnelSnapshot = {
  capturedAt: string;
  providerId: string;
  marketLabel: string;
  subjectArea?: number;
  subjectAreaBasis: MarketAreaBasis;
  subjectAskingPrice?: number;
  areaSimilarityMinimum: number;
  areaBand?: { min: number; max: number };
  hardEligibleCount: number;
  stages: {
    allMarket: MarketFunnelStage;
    similarArea: MarketFunnelStage;
    similarCondition: MarketFunnelStage;
    primaryComparables: MarketFunnelStage;
    marketCompetition: MarketFunnelStage;
    sameConditionMarket?: MarketFunnelStage;
    otherConditionAlternatives?: MarketFunnelStage;
  };
  rejectionReasonCounts: Record<MarketFunnelRejectionReason, number>;
  rejectionSamples: Partial<Record<MarketFunnelRejectionReason, MarketFunnelRejectionSample[]>>;
  cheaperSimilarAreaRejected: MarketFunnelRejectionSample[];
  conditionStageAvailable: boolean;
};

export type MarketValuationResult = {
  askingMarket?: MarketDatasetSummary;
  realizedMarket?: MarketDatasetSummary;
  recommendedListingPrice: number;
  marketLow: number;
  marketHigh: number;
  marketMid: number;
  ownerPricePosition: OwnerPricePosition;
  ownerDifferencePercent?: number;
  ownerDifferenceAmount?: number;
  confidenceScore: number;
  confidence: MarketConfidence;
  methodologyVersion: string;
  calculatedAt: string;
};

export type MarketStrategyKind = "quick-sale" | "recommended" | "market-test";

export type MarketStrategy = {
  kind: MarketStrategyKind;
  title: string;
  listingPrice: number;
  explanation: string;
};

export type MarketAnalysis = {
  id: string;
  userId: string;
  caseId: string;
  version: number;
  status: MarketAnalysisStatus;
  subjectSnapshot: MarketSubjectSnapshot;
  comparables: MarketAnalysisComparable[];
  marketFunnelSnapshot?: MarketFunnelSnapshot;
  resultSnapshot?: MarketValuationResult;
  strategySnapshot: MarketStrategy[];
  methodologyVersion: string;
  sourceCutoffAt: string;
  snapshotHash?: string;
  createdAt: string;
  updatedAt: string;
  finalizedAt?: string;
};

export type MarketProperty = MarketPropertySnapshot & {
  id: string;
  sourceProvider: string;
  sourcePropertyId?: string;
};

export type MarketListing = {
  id: string;
  propertyId: string;
  kind: "active" | "historical";
  askingPrice?: number;
  listedAt?: string;
  capturedAt: string;
  status: string;
  sourceName: string;
  sourceUrl?: string;
  externalId?: string;
  title?: string;
  city?: string;
  street?: string;
  locality?: string;
  propertyType?: string;
  rooms?: number;
  area?: number;
  pricePerM2?: number;
  condition?: string;
  electricity?: string;
  coreType?: string;
  kitchen?: string;
  floor?: number;
  totalFloors?: number;
  elevator?: string;
  balconyType?: string;
  cellar?: string;
  orientation?: string;
  buildingType?: string;
  reconstruction?: string;
  description?: string;
  agency?: string;
  municipalityId?: string;
  location?: MarketLocationHierarchy;
  areas?: MarketAreaModel;
  technical?: MarketTechnicalModel;
  equipment?: MarketEquipmentModel;
  canonicalPropertyType?: CanonicalMarketPropertyType;
  fieldProvenance?: MarketFieldProvenance;
  sources?: MarketListingSource[];
  sourceUrls?: string[];
  externalIds?: string[];
  dedupeReason?: string;
  dedupeConfidence?: MarketListingDedupeConfidence;
  dedupeConflicts?: MarketListingConflict[];
};

export type MarketListingDedupeConfidence = "EXACT_ID" | "HIGH_CONFIDENCE" | "POSSIBLE_DUPLICATE" | "DISTINCT";

export type MarketListingConflict = {
  field: string;
  values: Array<{
    providerId: string;
    sourceUrl: string;
    value: string | number;
  }>;
};

export type MarketListingSource = {
  providerId: string;
  sourceName: string;
  sourceUrl: string;
  externalId?: string;
  identityNamespace?: string;
  capturedAt: string;
};

export type MarketTransaction = {
  id: string;
  propertyId: string;
  realizedPrice: number;
  realizedAt: string;
  sourceName: string;
};
